import { Ref, ref, watch } from "vue"
import {
  AppNameEnum,
  BaseUser,
  GetMeResponse,
  UserTypeEnum,
  VitablePasswordlessAuthenticationResponse
} from "@vitable/asclepius-api-client-ts"
import useAsclepius from "@common/composables/api/asclepius"
import { version } from "../../package.json"
import { useUserStore } from "@/store/user-store"
import { useRouter } from "vue-router"

const APP_NAME: AppNameEnum = AppNameEnum.UserDashboard
const APP_VERSION: string = version

export function safeSameOriginPath(value: unknown): string | null {
  const path = Array.isArray(value) ? value[0] : value
  if (typeof path !== "string") return null
  if (!path.startsWith("/") || path.startsWith("//")) return null
  return path
}

export type PasswordlessLoginParams = {
  phone: string
  userType?: UserTypeEnum
  token?: string | null
  postLoginRedirectPageName?: string
  doNotAutoNavigateHome?: boolean
}

export function useLoginPasswordless() {
  const { getAuthenticationApi, callWithResponse, callResult } = useAsclepius()

  const isLoginLoading = ref(false)

  const userStore = useUserStore()
  const router = useRouter()

  const postLoginRedirectPageNameRef = ref<string>()
  const doNotAutoNavigateHome = ref<boolean>()

  async function loginPasswordless(params: PasswordlessLoginParams): Promise<void> {
    if (!params.phone) {
      throw Error("You need a phone to login!")
    }

    isLoginLoading.value = true

    postLoginRedirectPageNameRef.value = params.postLoginRedirectPageName
    doNotAutoNavigateHome.value = params.doNotAutoNavigateHome

    await callWithResponse(async () =>
      getAuthenticationApi().authenticateWithPasswordless({
        vitablePasswordlessAuthenticationRequest: {
          email_or_phone: params.phone,
          user_type: params.userType ?? UserTypeEnum.Member,
          token: params.token ?? null,
          app_name: APP_NAME,
          app_version: APP_VERSION
        }
      })
    )
  }

  watch(callResult, async (newResult) => {
    // @ts-ignore
    if (newResult.isSuccess()) {
      // @ts-ignore
      const data: VitablePasswordlessAuthenticationResponse = newResult.getData()
      if (!data.is_pending_passwordless_token) {
        await userStore.login(data.user!, data.token!, data.refresh_token!)
        const redirectedFromPath = safeSameOriginPath(router.currentRoute.value.query.redirectedFromPath)
        if (redirectedFromPath) {
          window.location.href = redirectedFromPath
        } else if (postLoginRedirectPageNameRef.value) {
          await router.push({ name: postLoginRedirectPageNameRef.value, query: router.currentRoute.value.query })
        } else if (!doNotAutoNavigateHome.value) {
          await router.push({ name: "home", query: router.currentRoute.value.query })
        }
      }
      isLoginLoading.value = false
      // @ts-ignore
    } else if (newResult.isFailure()) {
      isLoginLoading.value = false
    }
  })

  return {
    loginPasswordless,
    loginPasswordlessResult: callResult,
    isLoginLoading
  }
}

export function useRequestPasswordResetCode() {
  const { getAuthenticationApi, callWithResponse, callResult } = useAsclepius()

  async function requestPasswordResetCode(emailOrPhone: string): Promise<void> {
    await callWithResponse(async () => {
      const response = await getAuthenticationApi().requestPasswordChange({
        requestPasswordChangeRequest: { email_or_phone: emailOrPhone.trim(), user_type: UserTypeEnum.Member }
      })
      // A phone shared by several accounts cannot be resolved; the user must retry with their email
      if (response.data.challenge?.challenge_type !== "otp") {
        throw new Error("Please use the email address on your account to continue.")
      }
      return response
    })
  }

  return {
    requestPasswordResetCode,
    requestPasswordResetCodeResult: callResult
  }
}

export function useLoginAfterPasswordReset() {
  const { getAuthenticationApi } = useAsclepius()
  const userStore = useUserStore()

  // An explicit login with the freshly chosen password keeps the success
  // screen landing on the dashboard, matching the previous reset experience
  async function loginAfterPasswordReset(emailOrPhone: string, password: string): Promise<void> {
    const response = await getAuthenticationApi().passwordLogin({
      passwordLoginRequest: {
        email_or_phone: emailOrPhone.trim(),
        user_type: UserTypeEnum.Member,
        password,
        app_name: APP_NAME,
        app_version: APP_VERSION
      }
    })

    const session = response.data.login_session
    if (session) {
      await userStore.login(session.user, session.access_token, session.refresh_token)
    }
  }

  return { loginAfterPasswordReset }
}

export function useUpdatePassword() {
  const { getAuthenticationApi, callWithResponse, callResult } = useAsclepius()

  async function updatePassword(newPassword: string, code: string, emailOrPhone: string): Promise<void> {
    await callWithResponse(async () =>
      getAuthenticationApi().resetPassword({
        resetPasswordRequest: {
          new_password: newPassword,
          otp_code: code,
          email_or_phone: emailOrPhone.trim(),
          user_type: UserTypeEnum.Member
        }
      })
    )
  }

  return {
    updatePassword,
    updatePasswordResult: callResult
  }
}

export function useGetMe() {
  const { getAuthenticationApi, callWithResponse, callResult } = useAsclepius()
  const me: Ref<Nullable<BaseUser>> = ref(null)

  async function getMe(): Promise<void> {
    await callWithResponse(async () => getAuthenticationApi().getMe())
  }

  watch(callResult, (newResult) => {
    // @ts-ignore
    if (newResult.isSuccess()) {
      // @ts-ignore
      const data: GetMeResponse = newResult.getData()
      if (data.me) {
        me.value = data.me
      }
    }
  })

  return {
    me,
    getMe,
    getMeResult: callResult
  }
}
